import React from 'react';
import { useData } from '../contexts/DataContext';
import { Box, Typography } from '@mui/material';

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

export default function Speedometer() {
  const { stats, latest } = useData();

  const size = 400; // 🔧 adjust gauge size here (e.g. 300, 400, 500)
  const center = size / 2;
  const radius = center - size * 0.1; // keep 10% padding
  const maxSpeed = 50;
  const angleRange = 180;

  const currentSpeed = clamp(latest?.speed ?? 0, 0, maxSpeed);
  const avgSpeed = clamp(stats?.avgSpeed10 ?? 0, 0, maxSpeed);

  const angleForSpeed = (speed) => {
    const percent = speed / maxSpeed;
    return -90 + percent * angleRange;
  };

  const pointOnCircle = (angleDeg, r) => {
    const angle = (angleDeg * Math.PI) / 180;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    };
  };

  const renderNeedle = (angle, color, length, width = 3) => {
  const { x, y } = pointOnCircle(angle, length);
  return (
    <line
      x1={center}
      y1={center}
      x2={x}
      y2={y}
      stroke={color}
      strokeWidth={width}
      strokeLinecap="round"
      
    />
  );
};


  const renderTicks = () => {
    const ticks = [];
    for (let i = 0; i <= 10; i++) {
      const speed = (i / 10) * maxSpeed;
      const angle = angleForSpeed(speed);
      const outer = pointOnCircle(angle, radius + size * 0.02);
      const inner = pointOnCircle(angle, radius - size * 0.02);
      const label = pointOnCircle(angle, radius + size * 0.05);
      ticks.push(
        <g key={i}>
          <line
            x1={outer.x}
            y1={outer.y}
            x2={inner.x}
            y2={inner.y}
            stroke="#999"
            strokeWidth={1}
          />
          <text
            x={label.x}
            y={label.y}
            textAnchor="middle"
            fontSize={size * 0.03}
            fill="#666"
            transform={`rotate(${angle + 90}, ${label.x}, ${label.y})`}
          >
            {speed}
          </text>
        </g>
      );
    }
    return ticks;
  };

  return (
    <Box sx={{ textAlign: 'center', my: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          overflow: 'hidden',
          height: size * 0.53, // clip lower half
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ transform: 'rotate(-90deg)', display: 'block' }}
        >
          {/* Arc background */}
          <path
            d={`M ${pointOnCircle(-90, radius).x},${pointOnCircle(-90, radius).y}
               A ${radius} ${radius} 0 0 1 ${pointOnCircle(90, radius).x},${pointOnCircle(90, radius).y}`}
            fill="none"
            stroke="#eee"
            strokeWidth={size * 0.04}
          />

          {/* Ticks */}
          {renderTicks()}

          {/* Needles */}
          {renderNeedle(angleForSpeed(avgSpeed), "#ff9800", radius - size * 0.04, size * 0.013)}
          {renderNeedle(angleForSpeed(currentSpeed), "#1976d2", radius, size * 0.016)}

          {/* Center dot */}
          <circle cx={center} cy={center} r={size * 0.016} fill="#333" />
        </svg>
      </Box>

      {/* Speed below gauge */}
      <Box sx={{ mt: 1 }}>
        <Typography variant="h4" fontWeight="bold">
          {currentSpeed.toFixed(1)} km/h
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <span style={{ color: '#1976d2' }}>●</span> Huidig
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <span style={{ color: '#ff9800' }}>●</span> gem ({avgSpeed.toFixed(1)} km/h)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
